import ppfImg from "@/assets/services/ppf.jpg";
import ceramicImg from "@/assets/services/ceramic-coating.jpg";
import paintCorrectionImg from "@/assets/services/paint-correction.jpg";
import windowTintImg from "@/assets/services/window-tint.jpg";
import interiorImg from "@/assets/services/interior-detailing.jpg";
import exteriorImg from "@/assets/services/exterior-detailing.jpg";
import mobileImg from "@/assets/services/mobile-maintenance.jpg";

import ppfHero from "@/assets/ppf-hero.jpg";
import ceramicHero from "@/assets/ceramic-coating-hero.jpg";
import windowTintHero from "@/assets/window-tint-hero.jpg";
import detailingHero from "@/assets/detailing-hero.jpg";

export interface CategoryService {
  name: string;
  desc: string;
  href: string;
  image: string;
}

export interface AudienceItem {
  icon: string; // Lucide icon name
  title: string;
  description: string;
}

export interface ComparisonItem {
  title: string;
  description: string;
  href?: string;
}

export interface CategoryComparison {
  heading: string;
  subtitle: string;
  items: ComparisonItem[];
}

export interface Category {
  slug: string;
  label: string;
  tagline: string;
  headline: string;
  description: string;
  heroImage: string;
  services: CategoryService[];
  audience: AudienceItem[];
  comparison: CategoryComparison;
}

export const categories: Category[] = [
  {
    slug: "protect",
    label: "Protect",
    tagline: "Protect your finish",
    headline: "Shield Your Investment",
    description:
      "From rock chips to environmental contaminants, our protection services keep your vehicle's finish looking factory-fresh for years to come.",
    heroImage: ppfHero,
    services: [
      {
        name: "Paint Protection Film (PPF)",
        desc: "Stops chips and scratches before they happen",
        href: "/ppf",
        image: ppfImg,
      },
      {
        name: "Ceramic Coating",
        desc: "Long-lasting gloss and easier maintenance",
        href: "/ceramic-coating",
        image: ceramicImg,
      },
    ],
    audience: [
      {
        icon: "CarFront",
        title: "New Vehicle Owners",
        description:
          "Lock in that factory finish from day one — before the road takes its toll.",
      },
      {
        icon: "FileCheck",
        title: "Lease Returns",
        description:
          "Avoid costly damage charges by protecting paint from chips, stains, and swirls throughout your lease.",
      },
      {
        icon: "Route",
        title: "Road Warriors",
        description:
          "Highway commuters and road-trippers need protection that handles gravel, bugs, and harsh conditions.",
      },
    ],
    comparison: {
      heading: "PPF vs Ceramic Coating",
      subtitle:
        "Both protect — but in different ways. Here's how to choose.",
      items: [
        {
          title: "Paint Protection Film",
          description:
            "A physical barrier that absorbs rock chips, scratches, and road debris. Best for high-impact areas like hoods, bumpers, and mirrors.",
          href: "/ppf",
        },
        {
          title: "Ceramic Coating",
          description:
            "A chemical bond that repels water, dirt, and UV rays for a long-lasting deep gloss. Best for whole-vehicle hydrophobic protection and easy maintenance.",
          href: "/ceramic-coating",
        },
      ],
    },
  },
  {
    slug: "correct",
    label: "Correct",
    tagline: "Restore your paint",
    headline: "Bring Back the Brilliance",
    description:
      "Swirl marks, scratches, and oxidation don't stand a chance. Our paint correction services restore your vehicle's finish to better than showroom condition.",
    heroImage: detailingHero,
    services: [
      {
        name: "Paint Correction",
        desc: "Remove swirls, scratches, and haze",
        href: "/detailing-paint-correction",
        image: paintCorrectionImg,
      },
    ],
    audience: [
      {
        icon: "Repeat",
        title: "Selling or Trading In",
        description:
          "Maximize resale value by restoring your paint to a like-new finish before listing.",
      },
      {
        icon: "Eye",
        title: "Swirl Marks Bothering You",
        description:
          "Those spiderweb scratches visible in direct sunlight? We eliminate them completely.",
      },
      {
        icon: "ShoppingBag",
        title: "Post-Purchase Refresh",
        description:
          "Just bought a used vehicle? Start fresh with paint that looks better than the day it left the lot.",
      },
    ],
    comparison: {
      heading: "Signs You Need Paint Correction",
      subtitle:
        "Not sure if your paint needs professional attention? Look for these.",
      items: [
        {
          title: "Swirl Marks & Spider Webs",
          description:
            "Circular scratches visible in direct sunlight, typically caused by improper washing or automated car washes.",
        },
        {
          title: "Oxidation & Fading",
          description:
            "Dull, chalky paint that has lost its depth and gloss from prolonged UV exposure without protection.",
        },
        {
          title: "Water Spot Etching",
          description:
            "Mineral deposits from hard water that have etched into the clear coat, leaving permanent marks.",
        },
      ],
    },
  },
  {
    slug: "shade",
    label: "Shade",
    tagline: "Block heat and glare",
    headline: "Stay Cool, Drive Comfortable",
    description:
      "Professional window tinting reduces heat, blocks harmful UV rays, and adds privacy — all while enhancing your vehicle's appearance.",
    heroImage: windowTintHero,
    services: [
      {
        name: "Window Tint",
        desc: "Reduce heat, glare, and UV exposure",
        href: "/window-tint",
        image: windowTintImg,
      },
    ],
    audience: [
      {
        icon: "Thermometer",
        title: "Georgia Heat Sufferers",
        description:
          "Cut interior temps dramatically and stop your AC from working overtime in the Southern sun.",
      },
      {
        icon: "ShieldCheck",
        title: "Privacy Seekers",
        description:
          "Keep valuables out of sight and add a layer of security to your daily driver.",
      },
      {
        icon: "Sun",
        title: "UV-Sensitive Drivers",
        description:
          "Block up to 99% of harmful UV rays that cause skin damage and fade your interior.",
      },
    ],
    comparison: {
      heading: "Choosing the Right Tint Level",
      subtitle:
        "From barely-there to blacked-out — here's what to consider.",
      items: [
        {
          title: "Light Tint (50–70%)",
          description:
            "Subtle UV and glare reduction while maintaining maximum visibility. Great for windshields and front windows where legal limits apply.",
        },
        {
          title: "Medium Tint (25–35%)",
          description:
            "The sweet spot for most drivers — solid heat rejection, privacy, and a clean, refined look without going too dark.",
        },
        {
          title: "Dark Tint (5–20%)",
          description:
            "Maximum privacy and heat rejection for rear windows. The go-to for SUVs, trucks, and anyone who wants that murdered-out aesthetic.",
        },
      ],
    },
  },
  {
    slug: "shine",
    label: "Shine",
    tagline: "Keep your vehicle looking its best",
    headline: "Maintain That Fresh Detail Feel",
    description:
      "From deep interior cleaning to recurring mobile washes, our detailing services keep your vehicle looking its absolute best between major services.",
    heroImage: detailingHero,
    services: [
      {
        name: "Interior Detailing",
        desc: "Deep clean every interior surface",
        href: "/detailing-paint-correction",
        image: interiorImg,
      },
      {
        name: "Exterior Detailing",
        desc: "Restore exterior shine fast",
        href: "/detailing-paint-correction",
        image: exteriorImg,
      },
      {
        name: "Mobile Maintenance",
        desc: "Convenient recurring vehicle care",
        href: "/mobile-maintenance",
        image: mobileImg,
      },
    ],
    audience: [
      {
        icon: "Briefcase",
        title: "Busy Professionals",
        description:
          "No time to detail it yourself? We come to your home or office on your schedule.",
      },
      {
        icon: "Shield",
        title: "Post-Service Maintenance",
        description:
          "Already have PPF or ceramic coating? Regular maintenance keeps your investment performing at its peak.",
      },
      {
        icon: "Sparkles",
        title: "Detail Enthusiasts",
        description:
          "You care about the little things — so do we. Deep cleans that go far beyond a basic wash.",
      },
    ],
    comparison: {
      heading: "Which Detail Service Fits You?",
      subtitle:
        "From one-time deep cleans to ongoing maintenance — pick your level.",
      items: [
        {
          title: "Interior Detailing",
          description:
            "Full deep clean of every interior surface — leather conditioning, stain removal, and odor elimination for a cabin that feels brand new.",
          href: "/detailing-paint-correction",
        },
        {
          title: "Exterior Detailing",
          description:
            "Hand wash, clay bar, polish, and sealant. Restores your paint's depth and gloss without the commitment of a full correction.",
          href: "/detailing-paint-correction",
        },
        {
          title: "Mobile Maintenance",
          description:
            "Recurring washes delivered to your door — weekly, bi-weekly, or monthly. The easiest way to keep your vehicle showroom-ready.",
          href: "/mobile-maintenance",
        },
      ],
    },
  },
];
